// +----------------------------------------------------------------------
// | CRMEB [ CRMEB赋能开发者，助力企业发展 ]
// +----------------------------------------------------------------------
// | Copyright (c) 2016~2021 https://www.crmeb.com All rights reserved.
// +----------------------------------------------------------------------
// | Licensed CRMEB并不是自由软件，未经许可不能去掉CRMEB相关版权
// +----------------------------------------------------------------------
// | Author: CRMEB Team <admin@crmeb.com>
// +----------------------------------------------------------------------

import router from './router'
import store from './store'
import { Message } from 'element-ui'
import NProgress from 'nprogress' // progress bar
import 'nprogress/nprogress.css' // progress bar style
import { getToken } from '@/utils/auth' // get token from cookie
import getPageTitle from '@/utils/get-page-title'

NProgress.configure({ showSpinner: false }) // NProgress Configuration

const whiteList = ['/login', '/auth-redirect'] // no redirect whitelist

// 落地校正时免检的路径（不属于菜单体系、但登录后可能直接进入的页面）
const landingWhitelist = ['/401', '/404', '/setting/uploadPicture']

/**
 * 菜单是否"覆盖"该路径：路径本身或其上级在菜单里即视为有权限。
 * 用宽松前缀匹配——详情页/编辑页等子路径（如 /operation/roleManager/edit）不在菜单里，
 * 但它们以上级菜单 url 开头，必须放行；宁可少拦，不可把正常页面误跳。
 */
function menuCoversPath(routes, path) {
  return (routes || []).some(r => {
    const u = r.url || ''
    const hit = u.charAt(0) === '/' && (path === u || path.indexOf(u) === 0)
    return hit || (r.child && menuCoversPath(r.child, path))
  })
}

/**
 * 默认落地页：取"当前用户角色拥有的第一个菜单路由"。
 * 背景：/dashboard 是 constantRoutes 里的静态路由，任何账号都能打开，但它页面内的接口
 * 受后台菜单权限控制 —— 未分配"主页"菜单的账号会被接口返 403（request.js 弹"没有权限访问。"）。
 * 菜单树来自 GET /admin/getMenus（store.getters.permission_routes），
 * 结构为 { name, url(=路由地址), path, perms, child[] }。
 */
function firstAccessibleMenu(routes) {
  let fallback = ''
  const walk = (list) => {
    for (const r of list || []) {
      if (r.child && r.child.length) {
        const p = walk(r.child)
        if (p) return p
        continue
      }
      const url = r.url || ''
      // 只认站内绝对路径（排除外链与空 url）
      if (!url || url.charAt(0) !== '/' || /^https?:/i.test(url)) continue
      if (!fallback) fallback = url
      // 优先返回真实注册过的路由（避免落到菜单里的"空目录"而 404）
      try {
        const matched = (router.resolve(url).route.matched) || []
        if (matched.length && !matched.some(m => m.path === '*')) return url
      } catch (e) {
        // 解析失败则继续找下一个
      }
    }
    return ''
  }
  return walk(routes) || fallback
}

router.beforeEach(async(to, from, next) => {
  // start progress bar
  NProgress.start()

  // set page title
  document.title = getPageTitle(to.meta.title)

  // determine whether the user has logged in
  const hasToken = getToken()

  if (hasToken) {
    if (to.path === '/login') {
      // if is logged in, redirect to the home page
      next({ path: '/' })
      NProgress.done()
    } else {
      const hasRoles = store.getters.roles && store.getters.roles.length > 0
      if (hasRoles) {
        // “落地”场景才做菜单校正：默认首页（/ → /dashboard），或刚从登录页进来。
        // 正常点菜单 / 浏览器刷新不改行为，避免误伤详情页等非菜单路径。
        const isLanding = to.path === '/' || to.path === '/dashboard' || from.path === '/login'
        if (isLanding && landingWhitelist.indexOf(to.path) === -1 &&
            !menuCoversPath(store.getters.permission_routes, to.path)) {
          const first = firstAccessibleMenu(store.getters.permission_routes)
          if (first && first !== to.path) {
            next({ path: first, replace: true })
            NProgress.done()
            return
          }
          // 该角色一个菜单都没有：给 401，别让它卡在无权的页面上
          next('/401')
          NProgress.done()
          return
        }
        next()
      } else {
        try {
          const roles = await store.dispatch('user/getInfo')
          const accessRoutes = await store.dispatch('permission/generateRoutes', roles)
          router.addRoutes(accessRoutes)
          next({ ...to, replace: true })
        } catch (error) {
          // remove token and go to login page to re-login
          await store.dispatch('user/resetToken')
          Message.error(error || 'Has Error')
          next(`/login?redirect=${to.path}`)
          NProgress.done()
        }
      }
    }
  } else {
    /* has no token*/
    if (whiteList.indexOf(to.path) !== -1) {
      // in the free login whitelist, go directly
      next()
    } else {
      // other pages that do not have permission to access are redirected to the login page.
      next(`/login?redirect=${to.path}`)
      NProgress.done()
    }
  }
})

router.afterEach(() => {
  // finish progress bar
  NProgress.done()
})
